// @ts-check

import {
  allDrawingObjects,
  getDrawingObject
} from '../objects/drawing_object_registry.js?v=20260904f';
import { emit } from '../utils/event_bus.js?v=20260706d';
import { isPreparingProjectMutation } from '../project/project_mutation_scope.mjs';

let selectedIds = [];

function normalizeIds(ids = []) {
  return [...new Set((Array.isArray(ids) ? ids : [ids]).map(id => String(id || '').trim()).filter(Boolean))];
}

function setHighlight(object, selected) {
  if (!object || typeof object.highlight !== 'function') return;
  object.highlight(Boolean(selected), object);
}

function syncHighlights(previousIds = selectedIds, nextIds = selectedIds) {
  const previous = new Set(previousIds);
  const next = new Set(nextIds);
  for (const id of previous) {
    if (!next.has(id)) setHighlight(getDrawingObject(id), false);
  }
  for (const id of next) {
    if (!previous.has(id)) setHighlight(getDrawingObject(id), true);
  }
}

export function selectDrawingObject(id, options = {}) {
  const object = getDrawingObject(id);
  if (!object?.selectable) return null;
  if (isPreparingProjectMutation()) return object;
  const previous = selectedIds;
  selectedIds = options.append
    ? normalizeIds([...selectedIds, object.id])
    : [object.id];
  syncHighlights(previous, selectedIds);
  emit('object:selected', { object, selectedIds: [...selectedIds], append: Boolean(options.append) });
  return object;
}

export function setDrawingSelection(ids = []) {
  if (isPreparingProjectMutation()) {
    return normalizeIds(ids).map(id => getDrawingObject(id)).filter(object => object?.selectable);
  }
  const previous = selectedIds;
  selectedIds = normalizeIds(ids).filter(id => getDrawingObject(id)?.selectable);
  syncHighlights(previous, selectedIds);
  emit('object:selected', { selectedIds: [...selectedIds], objects: getSelectedDrawingObjects() });
  return getSelectedDrawingObjects();
}

export function clearDrawingSelection(options = {}) {
  if (isPreparingProjectMutation()) return [...selectedIds];
  const previous = selectedIds;
  selectedIds = [];
  syncHighlights(previous, selectedIds);
  if (options.emit !== false) emit('selection:cleared', { previousIds: previous });
  return previous;
}

export function getSelectedDrawingIds() {
  return [...selectedIds];
}

export function getSelectedDrawingObject() {
  return getDrawingObject(selectedIds[0]) || null;
}

export function getSelectedDrawingObjects() {
  return selectedIds.map(getDrawingObject).filter(Boolean);
}

export function hasDrawingSelection() {
  return getSelectedDrawingObjects().length > 0;
}

export function selectDrawingObjectsInBounds(bounds, mode = 'window') {
  if (!bounds) return [];
  const ids = allDrawingObjects()
    .filter(object => object.selectable && object.metadata?.bounds)
    .filter(object => {
      const objectBounds = object.metadata.bounds;
      if (mode === 'crossing') {
        return objectBounds.maxX >= bounds.minX
          && objectBounds.minX <= bounds.maxX
          && objectBounds.maxZ >= bounds.minZ
          && objectBounds.minZ <= bounds.maxZ;
      }
      return objectBounds.minX >= bounds.minX
        && objectBounds.maxX <= bounds.maxX
        && objectBounds.minZ >= bounds.minZ
        && objectBounds.maxZ <= bounds.maxZ;
    })
    .map(object => object.id);
  return setDrawingSelection(ids);
}
